import { Maybe } from 'graphql/jsutils/Maybe';
import { NavigateFunction } from 'react-router-dom';
import { Team } from '../../../gql/graphql';
import { fillPlaceholders, paths } from '../../../routing';
import { DocumentStatusEnum } from '../../../shared/constants/document';

export enum DocumentAccessLabelsEnum {
  EVERYONE = 'Everyone',
  TEAM = 'My Team',
}

export const getDocumentAccessLabel = (receiverTeam: Maybe<Team>) => {
  return receiverTeam ? DocumentAccessLabelsEnum.EVERYONE : DocumentAccessLabelsEnum.TEAM;
};

export const defineAlertableDocumentTooltipMessage = ({ documentStatus }: { documentStatus: DocumentStatusEnum }) => {
  const reviewingDocumentMessage = 'Send document for signing or request a review from your counterparty';
  const approvalPendingDocumentMessage = 'Approve or reject the pending internal approval for this document';
  const signingPrepDocumentMessage = 'Complete signing preparation and send the document for signing';

  if (documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN) {
    return signingPrepDocumentMessage;
  }

  return documentStatus === DocumentStatusEnum.REVIEWING ? reviewingDocumentMessage : approvalPendingDocumentMessage;
};

/**
 * Should opening this row be refused with the "the other party has it" notice?
 *
 * The refusal predates version snapshots, when a document you didn't hold was
 * genuinely unopenable. A DOCX-native document is different: `DocumentPage`
 * renders `AwaitingCounterpartyView` for the party without possession — the last
 * version they SENT, read-only, with a banner naming the holder. Blocking here
 * meant that view could never be reached from the deal page, so the feature
 * existed and was invisible.
 *
 * Legacy CKEditor documents keep the refusal: they have no snapshots, and
 * `DocumentPage` redirects them straight back to the deal page, so letting them
 * through would just bounce the user with no explanation.
 */
export const shouldBlockOpenWithoutPossession = ({
  documentStatus,
  hasCurrentTeamPossession,
  editorEngine,
}: {
  documentStatus: string;
  hasCurrentTeamPossession: boolean;
  /** `editorConfiguration.editor_engine` from the document row. */
  editorEngine?: string | null;
}): boolean => {
  if (hasCurrentTeamPossession) return false;
  if (documentStatus !== DocumentStatusEnum.REVIEWING) return false;

  return editorEngine !== 'docx_native';
};

export const handleDocumentNavigation = ({
  documentId,
  dealId,
  teamId,
  documentStatus,
  navigate,
}: {
  documentId: string;
  dealId: string;
  teamId: string;
  documentStatus: string;
  navigate: NavigateFunction;
}) => {
  // Documents in PREPARING_TO_SIGN or SIGNING status should go to the sign document page
  // SIGNING status goes to read-only sign view to show signing progress
  // PREPARING_TO_SIGN allows configuration of signers and sending for signing
  const shouldUseSignDocumentPath =
    documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN || documentStatus === DocumentStatusEnum.SIGNING;

  const urlPath = shouldUseSignDocumentPath ? paths.Documents.signDocument : paths.Documents.document;
  const url = fillPlaceholders(urlPath, { documentId, teamId, dealId });

  navigate(url);
};
